import { PlusIcon } from "@heroicons/react/20/solid";
import {
    BusinessProfileProblems,
    BusinessRepresentative,
    emptyBusinessRepresentative,
    extractEmailDomain,
    formatPhoneNumber,
    isPublicEmailDomain,
    representativePositionLabels,
    representativePositions,
} from "@sp-crm/core";
import { DeleteButton } from "components/ui/action-button";
import { Input } from "components/ui/input";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { Select } from "components/ui/select";
import React from "react";
import { useCurrentUser } from "store/selectors/hooks";
import {
    FieldHint,
    FieldProblem,
    FieldWarning,
    optionOrNull,
    StepHeading,
} from "./wizard-parts";
import { WizardStepProps } from "./wizard-step-props";

interface RepresentativeFieldsProps {
    representative: BusinessRepresentative;
    problemKey: "representative" | "secondRepresentative";
    problems: BusinessProfileProblems;
    onChange: (representative: BusinessRepresentative) => void;
}

const RepresentativeFields: React.FC<RepresentativeFieldsProps> = props => {
    const { representative, problemKey, problems, onChange } = props;
    const set = (changes: Partial<BusinessRepresentative>) =>
        onChange({ ...representative, ...changes });
    const problemFor = (field: keyof BusinessRepresentative): string | null =>
        problems[`${problemKey}.${field}`] ?? null;
    const emailProblem = problemFor("email");
    const phoneProblem = problemFor("phone");
    const freeEmail =
        !emailProblem && isPublicEmailDomain(extractEmailDomain(representative.email));

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
                <Input
                    label="First name"
                    value={representative.firstName}
                    onChange={e => set({ firstName: e.target.value })}
                />
                <FieldProblem problem={problemFor("firstName")} />
            </div>
            <div>
                <Input
                    label="Last name"
                    value={representative.lastName}
                    onChange={e => set({ lastName: e.target.value })}
                />
                <FieldProblem problem={problemFor("lastName")} />
            </div>
            <div>
                <Input
                    label="Job title"
                    value={representative.title}
                    placeholder="Owner"
                    onChange={e => set({ title: e.target.value })}
                />
                <FieldProblem problem={problemFor("title")} />
            </div>
            <div>
                <Select
                    label="Position"
                    value={representative.position ?? ""}
                    onChange={e =>
                        set({
                            position: optionOrNull(
                                representativePositions,
                                e.target.value,
                            ),
                        })
                    }>
                    <option value="">Choose the closest match</option>
                    {representativePositions.map(position => (
                        <option key={position} value={position}>
                            {representativePositionLabels[position]}
                        </option>
                    ))}
                </Select>
                <FieldProblem problem={problemFor("position")} />
            </div>
            <div>
                <Input
                    label="Work email"
                    type="email"
                    value={representative.email}
                    aria-invalid={!!emailProblem}
                    onChange={e => set({ email: e.target.value })}
                />
                <FieldProblem problem={emailProblem} />
                <FieldWarning
                    warning={
                        freeEmail
                            ? "An email at your business's own web address gets approved more often than a free one."
                            : null
                    }
                />
            </div>
            <div>
                <Input
                    label="Phone"
                    value={representative.phone}
                    placeholder="(512) 555-0100"
                    aria-invalid={!!phoneProblem}
                    onChange={e => set({ phone: formatPhoneNumber(e.target.value) })}
                />
                <FieldHint>A US number where carriers can reach this person.</FieldHint>
                <FieldProblem problem={phoneProblem} />
            </div>
        </div>
    );
};

const splitName = (name: string): { firstName: string; lastName: string } => {
    const [firstName, ...rest] = name.trim().split(/\s+/);
    return { firstName: firstName ?? "", lastName: rest.join(" ") };
};

export const representativeHeading = {
    title: "Authorized representative",
    description:
        "An owner or officer who can speak for the business. Carriers may contact them to confirm these details.",
};

export const Representatives: React.FC<WizardStepProps> = ({
    details,
    problems,
    onChange,
}) => {
    const { representative, secondRepresentative } = details;
    return (
        <>
            <RepresentativeFields
                representative={representative}
                problemKey="representative"
                problems={problems}
                onChange={changed => onChange({ representative: changed })}
            />
            {secondRepresentative ? (
                <div className="rounded-lg border border-gray-200 p-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                            Second representative
                        </span>
                        <DeleteButton
                            backgroundColor="bg-white"
                            label="Remove second representative"
                            confirm={
                                Object.values(secondRepresentative).some(Boolean)
                                    ? {
                                          title: "Remove the second representative?",
                                          message:
                                              "Everything you entered for this person will be removed.",
                                          confirmLabel: "Yes, remove",
                                      }
                                    : undefined
                            }
                            onClick={() => onChange({ secondRepresentative: null })}
                        />
                    </div>
                    <RepresentativeFields
                        representative={secondRepresentative}
                        problemKey="secondRepresentative"
                        problems={problems}
                        onChange={changed => onChange({ secondRepresentative: changed })}
                    />
                </div>
            ) : (
                <div>
                    <SecondaryCompactButton
                        type="button"
                        onClick={() =>
                            onChange({
                                secondRepresentative: emptyBusinessRepresentative(),
                            })
                        }>
                        <span className="inline-flex items-center gap-1">
                            <PlusIcon className="size-3.5" aria-hidden />
                            Add a second representative
                        </span>
                    </SecondaryCompactButton>
                    <FieldHint>
                        Optional. A backup contact in case carriers can&apos;t reach the
                        first person.
                    </FieldHint>
                </div>
            )}
        </>
    );
};

export const StepRepresentative: React.FC<WizardStepProps> = props => {
    const { details, onChange } = props;
    const user = useCurrentUser();

    const fillInMyDetails = () =>
        onChange({
            representative: {
                ...details.representative,
                ...splitName(user?.name ?? ""),
                email: user?.email ?? "",
                phone: formatPhoneNumber(user?.phone ?? ""),
            },
        });

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <StepHeading {...representativeHeading} />
                <SecondaryCompactButton type="button" onClick={fillInMyDetails}>
                    Use my details
                </SecondaryCompactButton>
            </div>
            <Representatives {...props} />
        </div>
    );
};
